import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import Button from './UI/Button';
import FullScreenPanel from './UI/FullScreenPanel';
import LoanLink from './LoanLink';
import ClientLink from './ClientLink';
import StatusBadge from './UI/StatusBadge';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';
import { useSortableTable, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from './UI/tableSort';

// The debit order collection file (EnDO) for chosen dates. The API picks the
// loans, the collection dates and amounts, and builds the file
// (GET /api/debit-orders/batch[/preview]); this shows what will be in it, the
// loans left out and why, and downloads it. The same file for the collections
// 4 days ahead is emailed every morning.

const isoDay = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const MODE_TAG = {
  reduced: ['reduced', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'],
  retry: ['full retry', 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300'],
  manual: ['manage manually', 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'],
};

const Tag = ({ className, title, children }) => (
  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${className}`} title={title}>{children}</span>
);

const TH = { className: `px-2.5 py-2 ${TABLE_HEAD_TEXT}` };
const TH_RIGHT = { ...TH, align: 'right' };
const TD = 'px-2.5 py-2.5 align-top';

const DebitOrderBatchModal = ({ onClose }) => {
  const [start, setStart] = useState(isoDay(4));
  const [end, setEnd] = useState(isoDay(4));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadPreview = useCallback(async (s, e) => {
    if (!s || !e) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(apiUrl('/api/debit-orders/batch/preview'), { params: { start: s, end: e }, headers: auth() });
      setPreview(res.data);
    } catch (err) {
      setPreview(null);
      setError(err?.response?.data?.error || 'The collections could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(start, end); }, [start, end, loadPreview]);

  const download = async () => {
    setDownloading(true);
    setError('');
    try {
      const res = await axios.get(apiUrl('/api/debit-orders/batch'), {
        params: { start, end },
        headers: auth(),
        responseType: 'blob',
      });
      const name = /filename="([^"]+)"/.exec(res.headers?.['content-disposition'] || '')?.[1] || preview?.file_name || 'EnDOREQ.csv';
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
      // Downloading records the collections; their amounts may now differ.
      loadPreview(start, end);
    } catch (err) {
      let msg = 'The collection file could not be downloaded. Please try again.';
      try {
        const text = await err?.response?.data?.text?.();
        msg = JSON.parse(text)?.error || msg;
      } catch (_) { /* keep the default */ }
      setError(msg);
    } finally {
      setDownloading(false);
    }
  };

  const setRange = (from, to) => { setStart(from); setEnd(to); };
  // By collection date until a column header is clicked.
  const byDate = (preview?.rows || []).slice().sort((a, b) => (a.collection_date < b.collection_date ? -1 : a.collection_date > b.collection_date ? 1 : 0));
  const { rows, sort, th } = useSortableTable(byDate);
  const skipped = preview?.skipped || [];
  const today = isoDay(0);

  const footer = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="text-xs text-gray-500 dark:text-slate-400">
        {preview?.count ? <>File <span className="font-mono">{preview.file_name}</span> · downloading records these collections</> : 'Nothing to download for these dates.'}
      </div>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={download} disabled={!preview?.count || downloading || loading}
          loading={downloading} busyLabel="Preparing…">
          Download CSV
        </Button>
      </div>
    </div>
  );

  return (
    <FullScreenPanel
      isOpen
      onClose={onClose}
      title="Debit Order Collection File"
      subtitle={preview ? `Deductions ${formatDateDMY(preview.start)} – ${formatDateDMY(preview.end)}` : 'EnDO file for the bank'}
      footer={footer}
    >
      <div className="space-y-4">
        {/* Dates and how the file is worked out. */}
        <section className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
              Deductions from
              <input type="date" value={start} min={today}
                onChange={(e) => { const v = e.target.value; setStart(v); if (end && v > end) setEnd(v); }}
                className="mt-1 block" />
            </label>
            <label className="text-xs font-medium text-gray-700 dark:text-slate-300">
              Deductions to
              <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
                className="mt-1 block" />
            </label>
            <div className="flex flex-wrap gap-1.5">
              {[
                ['In 4 days', isoDay(4), isoDay(4)],
                ['Next 7 days', today, isoDay(7)],
                ['Next 30 days', today, isoDay(30)],
              ].map(([label, f, t]) => (
                <button key={label} type="button" onClick={() => setRange(f, t)}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium border ${start === f && end === t
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'hover:bg-gray-50 dark:hover:bg-slate-600'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <ul className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-slate-300 list-disc pl-4">
            <li>Each active or bad debt loan is collected for its next deduction day between these dates.</li>
            <li>Collected on the deduction date, or 4 days before for a client who missed an instalment.</li>
            <li>A collection on a Saturday or Sunday moves to the Friday before; never before today.</li>
            <li>The bank keeps trying until 5 days after the deduction date (at most {preview?.max_tracking_days ?? 14} days).</li>
            <li>After two missed collections the amount is halved (at least N$250); the full instalment is tried again after three reduced collections.</li>
            <li>The file for the collections {preview?.days_ahead ?? 4} days ahead is also emailed every morning.</li>
          </ul>
        </section>

        {error && (
          <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300 text-sm" role="alert">{error}</div>
        )}

        {loading && !preview ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">Loading the collections…</p>
          </div>
        ) : preview && (
          <>
            <section className={`bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden ${loading ? 'opacity-60' : ''}`}>
              <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Collections in the file</h3>
                <span className="text-xs text-gray-500 dark:text-slate-400">{rows.length} loan{rows.length === 1 ? '' : 's'}{sort ? '' : ', by collection date'}</span>
              </div>
              {rows.length > 0 ? (
                <div>
                  <table className="w-full text-sm [overflow-wrap:anywhere]">
                    <thead>
                      <tr className={TABLE_HEAD_ROW}>
                        {th('Loan', 'loan', (r) => r.loan_code, TH)}
                        {th('Client', 'client', (r) => r.client_name, TH)}
                        {th('Bank account', 'bank', (r) => r.bank, TH)}
                        {th('Collect on', 'collect', (r) => r.collection_date, TH)}
                        {th('Tracking', 'tracking', (r) => Number(r.tracking_days), TH)}
                        {th('Amount', 'amount', (r) => Number(r.amount), TH_RIGHT)}
                        {th('Balance', 'balance', (r) => Number(r.outstanding), TH_RIGHT)}
                        {th('Left', 'left', (r) => Number(r.instalments_left), TH_RIGHT)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const tag = MODE_TAG[r.collection_mode];
                        return (
                          <tr key={r.loan_id} className={`${TABLE_ROW_LINE} hover:bg-gray-50/70 dark:hover:bg-slate-700/30`}>
                            <td className={TD}>
                              <LoanLink loanId={r.loan_id} className="font-medium">{r.loan_code}</LoanLink>
                              <div className="mt-1">
                                <StatusBadge status={r.loan_status} size="small" />
                              </div>
                            </td>
                            <td className={TD}>
                              <ClientLink clientId={r.client_id} name={r.client_name} />
                              <div className="text-[11px] text-gray-500 dark:text-slate-400">{r.client_code}</div>
                            </td>
                            <td className={TD}>
                              <div className="text-gray-800 dark:text-slate-200"><span className="font-semibold">{r.bank_code}</span> · {r.bank}</div>
                              <div className="text-[11px] text-gray-500 dark:text-slate-400">
                                {r.account_last4 ? <span className="font-mono">••••{r.account_last4}</span> : '-'} · {r.account_type || '-'}
                              </div>
                            </td>
                            <td className={TD}>
                              <div className="flex flex-wrap items-center gap-1.5 whitespace-nowrap">
                                <span className="font-medium">{formatDateDMY(r.collection_date)}</span>
                                {r.missed_instalment && <Tag className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">missed</Tag>}
                                {r.weekend_moved && <Tag className="bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">weekend</Tag>}
                              </div>
                              <div className="text-[11px] text-gray-500 dark:text-slate-400">Deduction {formatDateDMY(r.deduction_date)}</div>
                              <div className="text-[11px] text-gray-500 dark:text-slate-400">{r.collection_reason}</div>
                            </td>
                            <td className={`${TD} whitespace-nowrap`}>
                              <div>{r.tracking_days} day{r.tracking_days === 1 ? '' : 's'}</div>
                              <div className="text-[11px] text-gray-500 dark:text-slate-400">until {formatDateDMY(r.tracking_until)}</div>
                            </td>
                            <td className={`${TD} text-right whitespace-nowrap`}>
                              <div className="font-semibold text-gray-900 dark:text-slate-100">{toCurrency(r.amount)}</div>
                              {tag && <div className="mt-1"><Tag className={tag[1]} title={r.collection_note || undefined}>{tag[0]}</Tag></div>}
                              <div className="text-[11px] text-gray-500 dark:text-slate-400">
                                {r.amount > r.instalment ? 'whole balance' : r.amount < r.instalment ? `of ${toCurrency(r.instalment)}` : 'instalment'}
                              </div>
                            </td>
                            <td className={`${TD} text-right whitespace-nowrap`}>{toCurrency(r.outstanding)}</td>
                            <td className={`${TD} text-right`} title="Instalments left in the loan's term">{r.instalments_left}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-sm text-gray-600 dark:text-slate-300">No debit orders to collect for deductions between these dates.</div>
              )}
            </section>

            {skipped.length > 0 && (
              <section className="bg-white dark:bg-slate-800 rounded-xl border border-amber-200 dark:border-amber-900 shadow-sm overflow-hidden">
                <div className="h-1 bg-amber-500" />
                <div className="px-4 py-3">
                  <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                    {skipped.length} loan{skipped.length === 1 ? ' is' : 's are'} due but left out of the file
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">Update the client's details, then download the file again.</p>
                  <ul className="mt-2 divide-y divide-gray-100 dark:divide-slate-700 text-sm">
                    {skipped.map((x) => (
                      <li key={x.loan_id} className="py-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <LoanLink loanId={x.loan_id} className="font-medium">{x.loan_code}</LoanLink>
                        <span className="text-gray-800 dark:text-slate-200">{x.client_name}</span>
                        <span className="text-xs text-gray-500 dark:text-slate-400">deduction {formatDateDMY(x.deduction_date || x.collection_date)}</span>
                        <span className="text-xs text-red-700 dark:text-red-300">{x.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </FullScreenPanel>
  );
};

export default DebitOrderBatchModal;
